import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Youtube, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LOGO_URL_LIGHT } from "@/data/ekipData";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(t.common.success);
      setEmail("");
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const cols = [
    {
      title: t.footer.columns.group,
      links: [
        { label: t.nav.groupe, to: "/groupe" },
        { label: t.nav.impact, to: "/impact" },
        { label: t.nav.carrieres, to: "/carrieres" },
      ],
    },
    {
      title: t.footer.columns.ecosystem,
      links: [
        { label: t.nav.ecosysteme, to: "/ecosysteme" },
        { label: "KIDH", to: "/ecosysteme#kidh" },
        { label: "Kip Energy", to: "/ecosysteme#kip-energy" },
        { label: "EKIPTECH", to: "/ecosysteme#ekiptech" },
        { label: "KIPAY", to: "/ecosysteme#kipay" },
      ],
    },
    {
      title: t.footer.columns.projects,
      links: [
        { label: t.nav.projets, to: "/projets" },
        { label: "Vision 2035", to: "/projets#vision-2035" },
        { label: "Réseau Wayne", to: "/projets#reseau-wayne" },
        { label: "Sassandra", to: "/projets#sassandra" },
      ],
    },
    {
      title: t.footer.columns.contact,
      links: [
        { label: t.nav.contact, to: "/contact" },
        { label: t.nav.actualites, to: "/actualites" },
      ],
    },
  ];

  return (
    <footer
      data-testid="ekip-footer"
      className="relative bg-ekip-navy-deep text-white pt-20 pb-8"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-14 border-b border-ekip-gold/20">
          <div className="md:col-span-4">
            <Link to="/" className="inline-block group">
              <img
                src={LOGO_URL_LIGHT}
                alt="EKIP Holding Company"
                className="h-32 md:h-40 w-auto object-contain group-hover:opacity-90 transition-opacity"
              />
            </Link>
            <p className="mt-6 text-sm text-white/70 leading-relaxed max-w-sm font-body font-light">
              {t.footer.about}
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[Linkedin, Twitter, Youtube].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="w-10 h-10 border border-ekip-gold/30 flex items-center justify-center text-ekip-copper hover:bg-ekip-copper hover:text-white hover:border-ekip-copper transition-colors"
                  aria-label="Social"
                >
                  <Icon size={15} strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.title} className="md:col-span-2">
              <span className="text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
                {c.title}
              </span>
              <ul className="mt-5 space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      data-testid={`footer-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm text-white/70 hover:text-ekip-copper transition-colors font-body font-light"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter + address */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-10 border-b border-ekip-gold/20">
          <div className="md:col-span-6">
            <span className="text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
              Newsletter
            </span>
            <p className="mt-3 font-display text-xl md:text-2xl font-medium leading-snug">
              {t.footer.newsletter}
            </p>
            <form
              onSubmit={submit}
              data-testid="footer-newsletter-form"
              className="mt-5 flex flex-col sm:flex-row items-stretch gap-3 max-w-md"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                data-testid="footer-newsletter-email"
                className="flex-1 bg-transparent border border-ekip-gold/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-ekip-copper outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="inline-flex items-center justify-center gap-2 bg-ekip-copper text-white px-5 py-3 text-xs uppercase tracking-[0.22em] font-medium hover:bg-ekip-copper-hover transition-colors disabled:opacity-60"
              >
                {loading ? t.common.submitting : t.footer.newsletterCta}
                <ArrowRight size={14} />
              </button>
            </form>
          </div>
          <div className="md:col-span-6 md:text-right">
            <span className="text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
              {t.contact.coords.addressLabel}
            </span>
            <p className="mt-3 font-display text-xl md:text-2xl font-medium">
              {t.contact.coords.address[0]}
            </p>
            <p className="text-sm text-white/70 mt-1">
              {t.contact.coords.address[1]}
            </p>
            <p className="text-sm text-white/70 mt-3">
              {t.contact.coords.emails[0]} · {t.contact.coords.phones[0]}
            </p>
          </div>
        </div>

        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <span className="text-xs text-white/50 font-body">
            {t.footer.copyright}
          </span>
          <div className="flex items-center gap-5 text-xs text-white/50">
            <a href="#" className="hover:text-ekip-copper transition-colors">{t.footer.legal}</a>
            <span className="text-ekip-gold/30">·</span>
            <a href="#" className="hover:text-ekip-copper transition-colors">{t.footer.privacy}</a>
            <span className="text-ekip-gold/30">·</span>
            <a href="#" className="hover:text-ekip-copper transition-colors">{t.footer.sitemap}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
